import * as React from "react";

import RefreshIcon from "@mui/icons-material/Refresh";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const GREEN = "#3CE97C";

interface ThumbProps {
    label: string;
    selected: boolean;
    dimmed: boolean;
    onClick: () => void;
    children: React.ReactNode;
}

function Thumb({
    label,
    selected,
    dimmed,
    onClick,
    children,
}: ThumbProps): React.JSX.Element {
    return (
        <Box
            component="button"
            type="button"
            aria-label={label}
            aria-pressed={selected}
            onClick={onClick}
            sx={{
                width: 44,
                height: 44,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: selected ? "rgba(60,233,124,0.10)" : "transparent",
                border: selected
                    ? `1.5px solid ${GREEN}`
                    : "1px solid rgba(168,184,217,0.25)",
                color: selected ? GREEN : dimmed ? "#55658A" : "#C3CFE6",
                cursor: "pointer",
            }}
        >
            {children}
        </Box>
    );
}

interface BranchChoiceProps {
    title: string;
    detail: string;
    tint: string;
    icon: React.ReactNode;
    onClick?: () => void;
}

function BranchChoice({
    title,
    detail,
    tint,
    icon,
    onClick,
}: BranchChoiceProps): React.JSX.Element {
    return (
        <Stack
            component="button"
            type="button"
            direction="row"
            onClick={onClick}
            sx={{
                alignItems: "center",
                gap: 1.5,
                p: 1,
                width: "100%",
                border: 0,
                borderRadius: 1.5,
                bgcolor: "transparent",
                textAlign: "left",
                cursor: "pointer",
                fontFamily: "inherit",
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: "50%",
                    bgcolor: tint,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: 14.5,
                        fontWeight: 600,
                        color: "#fff",
                        lineHeight: 1.25,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{ fontSize: 12.5, color: "#94A3B8", mt: "2px" }}
                >
                    {detail}
                </Typography>
            </Box>
        </Stack>
    );
}

interface RatingRowProps {
    intent: "RECOMMEND" | "HELP";
    helpful?: boolean;
    onRate: (helpful: boolean) => void;
    /** Put the cursor in the composer so they can say what was missing. */
    onRetry: () => void;
    /** Record that the user chose a human after an answer missed. */
    onEscalate: () => void;
}

/**
 * "Did this help?" under an answer, and what happens when the answer is no.
 *
 * A thumb down is the most useful event in the feature and the worst place to
 * stop, so it opens rather than closes: the fork below keeps the conversation
 * going, and the retry is better than the first attempt precisely because it
 * knows what failed.
 *
 * What the fork offers depends on the intent. A missed support answer can go to
 * a human; a missed recommendation cannot — "message the team" under a dinking
 * drill is the same non sequitur the old chip footer used to commit — so that
 * side offers another go instead.
 */
export default function RatingRow({
    intent,
    helpful,
    onRate,
    onRetry,
    onEscalate,
}: RatingRowProps): React.JSX.Element {
    const rated = helpful !== undefined;

    return (
        <Box sx={{ px: 2, pl: "52px", mb: 1.75 }}>
            <Typography sx={{ fontSize: 12.5, color: "#94A3B8", mb: 1.25 }}>
                {helpful === true ? "Glad that helped." : "Did this help?"}
            </Typography>
            <Stack direction="row" sx={{ gap: 1.25 }}>
                <Thumb
                    label="This helped"
                    selected={helpful === true}
                    dimmed={rated}
                    onClick={() => onRate(true)}
                >
                    <ThumbUpOffAltIcon sx={{ fontSize: 19 }} />
                </Thumb>
                <Thumb
                    label="This did not help"
                    selected={helpful === false}
                    dimmed={rated}
                    onClick={() => onRate(false)}
                >
                    <ThumbDownOffAltIcon sx={{ fontSize: 19 }} />
                </Thumb>
            </Stack>

            {helpful === false && (
                <Box
                    sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.045)",
                        border: "1px solid rgba(168,184,217,0.18)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14.5,
                            color: "#E2E8F0",
                            lineHeight: 1.4,
                            mb: 1.25,
                        }}
                    >
                        Sorry — that missed. Tell me what is actually happening
                        and I&apos;ll try again
                        {intent === "HELP"
                            ? ", or I can get a person on it."
                            : "."}
                    </Typography>
                    <BranchChoice
                        title="Let me try again"
                        detail="Say what is missing and I'll have another go"
                        tint="rgba(60,233,124,0.12)"
                        icon={
                            <RefreshIcon sx={{ fontSize: 18, color: GREEN }} />
                        }
                        onClick={onRetry}
                    />
                    {intent === "HELP" && (
                        <BranchChoice
                            title="Message the team"
                            detail="Sends this conversation so you don't repeat it"
                            tint="rgba(90,162,255,0.14)"
                            icon={
                                <SupportAgentIcon
                                    sx={{ fontSize: 18, color: "#7FB4FF" }}
                                />
                            }
                            onClick={onEscalate}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
}
